import { api } from '../api';
import { invalidator, provider, tags } from '../tags';
import type {
  Account,
  AirsConsumptionVisualizer,
  ApiError,
  AzStorage,
  AzureTechnicalAdvisory,
  Invitation,
  LevelUpSummary,
  operations,
  UserAccount,
} from '../types';

type DestroyInvitationArgs = operations['destroyInvitation']['parameters']['path'];
type DestroyTeamMemberArgs = operations['destroyUserAccount']['parameters']['path'];
type DestroyAccountArgs = operations['destroyAccount']['parameters']['path'];

/* istanbul ignore file */

export const accountApi = api.injectEndpoints({
  overrideExisting: false,
  endpoints: (builder) => ({
    accounts: builder.query<Array<Account>, void>({
      query: () => 'api/account/',
      providesTags: provider<Account>(tags.account),
    }),

    account: builder.query<Account, string>({
      query: (id) => `api/account/${id}/`,
      providesTags: provider<Account>(tags.account),
    }),

    updateAccount: builder.mutation<Account, Partial<Account> & { id: string }>({
      query: ({ id, ...patch }) => ({
        url: `api/account/${id}/`,
        method: 'PATCH',
        body: {
          ...patch,
        },
      }),
      invalidatesTags: (
        result: Account | void,
        error: ApiError | undefined,
        args: Partial<Account> & { id: string }
      ) => {
        return [
          ...invalidator<Account, Partial<Account> & { id: string }>(tags.account, undefined, 'id')(
            result,
            error,
            args
          ),
          tags.levelup,
        ];
      },
    }),

    teamMembers: builder.query<Array<UserAccount>, string>({
      query: (id) => `api/account/${id}/user/`,
      providesTags: provider<UserAccount>(tags.userDetail, 'id'),
    }),

    destroyTeamMember: builder.mutation<void, DestroyTeamMemberArgs>({
      query: ({ account_pk, id }) => ({
        url: `api/account/${account_pk}/user/${id}/`,
        method: 'DELETE',
      }),
      invalidatesTags: invalidator<void, DestroyTeamMemberArgs>(tags.userDetail, undefined, 'id'),
    }),

    transferAdmin: builder.mutation<void, { account_id: string; new_admin_id: string }>({
      query: ({ account_id, new_admin_id }) => ({
        url: `api/account/${account_id}/transfer_admin/`,
        method: 'POST',
        body: { new_admin_id },
      }),
      invalidatesTags: invalidator<void, { account_id: string; new_admin_id: string }>(tags.account, undefined, 'id'),
    }),

    invitations: builder.query<Array<Invitation>, string>({
      query: (id) => `api/account/${id}/invitation/`,
      providesTags: provider<Invitation>(tags.invitation, 'invitee'),
    }),

    invitation: builder.mutation<Invitation, { id: string; invitee: string }>({
      query: ({ id, invitee }) => ({
        url: `api/account/${id}/invitation/`,
        body: { invitee },
        method: 'POST',
      }),
      invalidatesTags: invalidator<Invitation, Invitation>(tags.invitation),
    }),

    destroyInvitation: builder.mutation<void, DestroyInvitationArgs>({
      query: ({ id, account_pk }) => ({
        url: `api/account/${account_pk}/invitation/${id}/`,
        method: 'DELETE',
      }),
      invalidatesTags: invalidator<void, DestroyInvitationArgs>(tags.invitation),
    }),

    destroyAccount: builder.mutation<void, DestroyAccountArgs>({
      query: ({ id }) => ({
        url: `api/account/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: Object.values(tags),
    }),

    storageSAS: builder.mutation<AzStorage, string>({
      query: (id) => ({
        url: `api/account/${id}/sas/`,
        method: 'POST',
      }),
    }),

    azureConsumption: builder.query<AirsConsumptionVisualizer, string>({
      query: (id) => `api/account/${id}/azure_consumption/`,
    }),

    levelupSummary: builder.query<LevelUpSummary, string>({
      query: (id) => `api/account/${id}/levelup_summary/`,
      providesTags: [tags.levelup],
    }),

    ataRequests: builder.query<Array<AzureTechnicalAdvisory>, string>({
      query: (id) => `api/account/${id}/ata/`,
      providesTags: provider<AzureTechnicalAdvisory>(tags.ataRequest, 'case_number'),
    }),
  }),
});
